// Reconciliation — does the far side agree with Atlas about what was sent?
//
// Instance D computes it (reconciliation_service); this panel only renders it, under
// the queue on the Submit queue page. Two lists matter:
//
//   * "Atlas says sent, the far side has no record" (sent_unacknowledged)
//   * "The far side has it, Atlas does not"          (seen_unrecorded)
//
// plus an advisory list of queue rows that have sat too long. A person who has looked
// at a mismatch acknowledges it with a reason, which D records as a comment on the
// claim so it stops reappearing.
//
// An empty list must never look like a clean bill of health when Atlas could not
// check: no feed, no far side, or nothing recorded yet each get their own sentence.
// PHI: claim id, DOS, payer and D's fixed sentence only.
//
// POST …/reconciliation/run is deliberately not wired (ruling 2026-09-13): it can race the
// 11:00 clearinghouse sync on the same Claim.MD cursor into duplicate shell claims; the
// nightly job refreshes instead.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { acknowledgeMismatch, fetchMismatches, parseServerTime } from "./api";
import { relativeTime } from "./ageing";
import useReturnFocus from "./useReturnFocus";
import "./Queue.css";

const FAR_SIDE_LABEL = {
  stedi: "Stedi",
  claimmd: "Claim.MD",
  cobalt: "eCW (Cobalt sync)",
  csv: "the practice's CSV imports",
};

const MAX_REASON = 500; // reconciliation_service.MAX_ACK_REASON_LENGTH

// What Atlas can and cannot see for this practice, as one sentence and a tone.
export function farSideState(result, now = new Date()) {
  const { key, lastRefreshedAt, refreshable } = result.farSide;
  if (!key) {
    return {
      tone: "grey",
      text: "Nothing connected to this practice can confirm what was sent, so Atlas cannot check it. The lists below stay empty for that reason, not because everything matches.",
    };
  }
  const label = FAR_SIDE_LABEL[key] || key;
  if (key === "claimmd" && !refreshable) {
    return {
      tone: "amber",
      text: "No Claim.MD feed for this practice: no Claim.MD key is stored, so Claim.MD's answers are not being read. A submission can look unanswered when it was accepted.",
    };
  }
  // Stedi pushes its answers by webhook; there is no refresh stamp to age.
  if (key === "stedi") {
    return {
      tone: "grey",
      text: `Checked against ${label}, which reports by webhook.`,
    };
  }
  if (!lastRefreshedAt) {
    return {
      tone: "amber",
      text: `Checked against ${label}, which has never been refreshed for this practice.`,
    };
  }
  // A refresh older than the far side's own answer window cannot have answered
  // recent submissions, so an empty list under it is not reassuring.
  const refreshed = parseServerTime(lastRefreshedAt);
  const limit = Number(result.thresholds?.[key] ?? result.thresholds?.default);
  const ageDays = refreshed
    ? (now.getTime() - refreshed.getTime()) / 86400000
    : 0;
  if (Number.isFinite(limit) && ageDays > limit) {
    return {
      tone: "amber",
      text: `Checked against ${label}, last refreshed ${relativeTime(refreshed, now)} — older than the ${limit}-day window, so recent submissions cannot have been answered yet.`,
    };
  }
  return {
    tone: "grey",
    text: `Checked against ${label}, last refreshed ${relativeTime(refreshed, now)}.`,
  };
}

// What an empty list means. "None." only when Atlas could actually look.
export function emptyListText(kind, result, now = new Date()) {
  const { key, refreshable } = result.farSide;
  if (!key) return "Not checked — nothing can confirm what was sent.";
  if (key === "claimmd" && !refreshable)
    return "Not checked — no Claim.MD feed.";
  if (!result.recordingSince) {
    return kind === "seen_unrecorded"
      ? "Not checked — nothing recorded yet."
      : "Nothing has been recorded as sent yet.";
  }
  // Under an amber banner (a feed that is stale or has never refreshed) an empty
  // list is only as good as the last refresh, and says so.
  if (farSideState(result, now).tone === "amber") {
    return "None as of the last refresh.";
  }
  return "None.";
}

function AcknowledgeModal({ practiceId, entry, kind, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const reasonRef = useRef(null);

  useReturnFocus(reasonRef);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const valid = reason.trim().length > 0;

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      await acknowledgeMismatch(practiceId, {
        claimId: entry.claimId,
        kind,
        reason: reason.trim(),
      });
      onDone(entry);
    } catch (err) {
      setError(err.message);
      setBusy(false);
      setTimeout(() => reasonRef.current?.focus(), 0);
    }
  };

  return (
    <div className="sq-modal-backdrop" onClick={() => !busy && onClose()}>
      <div
        className="sq-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sq-ack-title"
      >
        <div className="sq-modal-header">
          <h2 id="sq-ack-title">Acknowledge mismatch</h2>
          <button
            className="sq-modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="sq-modal-body">
          <div className="sq-modal-summary">
            <span>
              Claim <strong>#{entry.claimId}</strong>
            </span>
            <span>DOS {entry.dateOfService || "—"}</span>
            <span>{entry.payerName || "—"}</span>
          </div>
          {entry.detail && <p className="sq-modal-lede">{entry.detail}</p>}
          <p className="sq-modal-lede">
            This records that you have looked into it, as a comment on the
            claim, and stops it being listed. It does not change the claim or
            any submission.
          </p>
          <label className="sq-field">
            <span>What you found</span>
            <textarea
              ref={reasonRef}
              rows={3}
              maxLength={MAX_REASON}
              value={reason}
              disabled={busy}
              placeholder="e.g. payer confirmed receipt by phone, ICN 2026091300123"
              onChange={(e) => setReason(e.target.value)}
            />
            <small className="sq-field-hint">
              Saved on the claim. Claim ids and payer detail only — no patient
              names.
            </small>
          </label>
          {error && (
            <div className="sq-message error" role="alert">
              {error}
            </div>
          )}
        </div>
        <div className="sq-modal-footer">
          <button
            className="btn btn-secondary btn-sm"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={submit}
            disabled={!valid || busy}
          >
            {busy ? "Saving…" : `Acknowledge claim #${entry.claimId}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function MismatchTable({ entries, kind, readOnly, onAcknowledge, daysLabel }) {
  return (
    <div className="sq-table-wrap">
      <table className="sq-table">
        <thead>
          <tr>
            <th>Claim</th>
            <th>DOS</th>
            <th>Payer</th>
            <th>Via</th>
            <th className="num">{daysLabel}</th>
            <th>What Atlas sees</th>
            {!readOnly && kind && <th aria-label="Actions" />}
          </tr>
        </thead>
        <tbody>
          {entries.map((e, i) => (
            <tr key={`${e.claimId}-${e.eventId ?? i}`}>
              <td className="sq-claim">#{e.claimId ?? "—"}</td>
              <td className="sq-nowrap">{e.dateOfService || "—"}</td>
              <td>{e.payerName || "—"}</td>
              <td>{e.channel || "—"}</td>
              <td className="num">{e.days ?? "—"}</td>
              <td className="sq-recon-detail">{e.detail || "—"}</td>
              {!readOnly && kind && (
                <td className="sq-actions">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => onAcknowledge(e, kind)}
                  >
                    Acknowledge
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ReconciliationPanel({ practiceId, readOnly = false }) {
  // undefined = not loaded; null = no feed (404); object = result
  const [result, setResult] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [acking, setAcking] = useState(null); // {entry, kind}
  const ticket = useRef(0);

  const load = useCallback(async () => {
    if (!practiceId) return;
    const mine = (ticket.current += 1);
    setLoading(true);
    setError(null);
    try {
      const body = await fetchMismatches(practiceId);
      if (mine !== ticket.current) return;
      setResult(body);
    } catch (err) {
      if (mine !== ticket.current) return;
      setError(err.message);
      setResult(undefined);
    } finally {
      if (mine === ticket.current) setLoading(false);
    }
  }, [practiceId]);

  useEffect(() => {
    setMessage(null);
    setAcking(null);
    load();
  }, [load]);

  const onAcknowledged = (entry) => {
    setAcking(null);
    setMessage(`Claim #${entry.claimId} acknowledged.`);
    load();
  };

  return (
    <section className="sq-recon">
      <div className="sq-tab-toolbar">
        <h3 className="sq-section-title sq-recon-title">Reconciliation</h3>
        <span className="sq-skill-copy">
          {result && result.checkedAt && (
            <span className="sq-muted">
              checked {relativeTime(result.checkedAt)}
            </span>
          )}
          <button
            className="btn btn-secondary btn-sm"
            onClick={load}
            disabled={loading}
          >
            {loading ? "Checking…" : "Check again"}
          </button>
        </span>
      </div>

      {message && (
        <div className="sq-message success">
          <span>{message}</span>
          <button
            className="sq-message-close"
            aria-label="Dismiss"
            onClick={() => setMessage(null)}
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="sq-message error" role="alert">
          <span>{error}</span>
        </div>
      )}

      {loading && result === undefined && !error && (
        <p className="sq-empty">Checking…</p>
      )}

      {result === null && (
        <div className="sq-run-banner grey">
          <span>No reconciliation feed yet.</span>
        </div>
      )}

      {result && (
        <ReconciliationBody
          result={result}
          readOnly={readOnly}
          onAcknowledge={(entry, kind) => setAcking({ entry, kind })}
        />
      )}

      {acking && (
        <AcknowledgeModal
          practiceId={practiceId}
          entry={acking.entry}
          kind={acking.kind}
          onClose={() => setAcking(null)}
          onDone={onAcknowledged}
        />
      )}
    </section>
  );
}

function ReconciliationBody({ result, readOnly, onAcknowledge }) {
  const far = farSideState(result);
  const recording = !!result.recordingSince;
  return (
    <>
      <div className={`sq-run-banner ${far.tone}`}>
        <span>{far.text}</span>
      </div>

      {!recording && (
        <p className="sq-muted sq-recon-note">
          No submission has been recorded for this practice yet, so there is
          nothing to compare. This is not the same as everything matching.
        </p>
      )}

      <h4 className="sq-recon-list-title">
        Atlas says sent, the far side has no record
        <span className="sq-muted"> · {result.sentUnacknowledged.length}</span>
      </h4>
      {result.sentUnacknowledged.length > 0 ? (
        <MismatchTable
          entries={result.sentUnacknowledged}
          kind="sent_unacknowledged"
          readOnly={readOnly}
          onAcknowledge={onAcknowledge}
          daysLabel="Days since sent"
        />
      ) : (
        <p className="sq-muted sq-recon-note">
          {emptyListText("sent_unacknowledged", result)}
        </p>
      )}

      <h4 className="sq-recon-list-title">
        The far side has it, Atlas does not
        <span className="sq-muted"> · {result.seenUnrecorded.length}</span>
      </h4>
      {result.seenUnrecorded.length > 0 ? (
        <MismatchTable
          entries={result.seenUnrecorded}
          kind="seen_unrecorded"
          readOnly={readOnly}
          onAcknowledge={onAcknowledge}
          daysLabel="Days"
        />
      ) : (
        <p className="sq-muted sq-recon-note">
          {emptyListText("seen_unrecorded", result)}
        </p>
      )}

      {result.queuedStale.length > 0 && (
        <>
          <h4 className="sq-recon-list-title">
            Sitting in the queue too long
            <span className="sq-muted"> · {result.queuedStale.length}</span>
          </h4>
          <MismatchTable
            entries={result.queuedStale}
            kind={null}
            readOnly={readOnly}
            daysLabel="Days"
          />
        </>
      )}

      {result.acknowledgedCount > 0 && (
        <p className="sq-muted sq-recon-note">
          {result.acknowledgedCount} acknowledged mismatch
          {result.acknowledgedCount === 1 ? " is" : "es are"} not listed.
        </p>
      )}
    </>
  );
}
